<p-dialog
  [(visible)]="visible"
  (visibleChange)="visibleChange.emit($event)"
  [modal]="true"
  [closable]="true"
  [dismissableMask]="true"
  [draggable]="false"
  [style]="{ width: '860px' }"
  [breakpoints]="{ '920px': '96vw' }"
  styleClass="wl-epic-dialog">

  <!-- El encabezado no scrollea: con la lista de tareas larga, el título de la épica y su avance
       tienen que seguir a la vista para saber de qué se está mirando el desglose. -->
  <ng-template pTemplate="header">
    <div class="ed-head" *ngIf="row as r">
      <span class="ed-cat" *ngIf="category" [style.color]="category.color">
        <i [class]="category.icon"></i> {{ category.name }}
      </span>
      <!-- El estado va pegado al título y no al margen derecho: junto a la ✕ del diálogo quedaba
           alineado contra un botón que no es suyo, y de lejos se leía como parte del cerrar. -->
      <h2 class="ed-title" *ngIf="!editandoTitulo" [title]="r.epic.title">
        <i class="fa-solid fa-layer-group" [style.color]="accent"></i>
        <span class="ed-title-txt">{{ r.epic.title }}</span>
        <!-- El lápiz va pegado al título, no en una barra de acciones: lo que se corrige es ESTE
             texto, y a un metro de distancia habría que adivinar sobre qué actúa. -->
        <button type="button" class="ed-rename" *ngIf="isDev" (click)="empezarRenombrar()"
          title="Corregir el nombre de la épica">
          <i class="fa-solid fa-pen"></i>
        </button>
        <span class="ed-status" [style.background]="EPIC_STATUS_META[r.status].color"
          title="Se deriva del trabajo de sus tareas, no se marca a mano">
          <i [class]="EPIC_STATUS_META[r.status].icon"></i> {{ EPIC_STATUS_META[r.status].label }}
        </span>
      </h2>

      <!-- Edición en sitio: la caja ocupa el lugar exacto del título para que el cambio se vea
           donde va a quedar. Enter guarda, Escape cancela — es una corrección de una línea. -->
      <div class="ed-title-edit" *ngIf="editandoTitulo">
        <i class="fa-solid fa-layer-group" [style.color]="accent"></i>
        <input type="text" pInputText class="ed-title-input" [(ngModel)]="tituloBorrador"
          [disabled]="guardando" placeholder="Nombre de la épica" maxlength="120" autofocus
          (keyup.enter)="guardarTitulo()" (keyup.escape)="cancelarRenombrar()" />
        <button type="button" class="ed-ok" (click)="guardarTitulo()" [disabled]="guardando" title="Guardar">
          <i class="fa-solid" [class.fa-check]="!guardando" [class.fa-spinner]="guardando" [class.fa-spin]="guardando"></i>
        </button>
        <button type="button" class="ed-cancel" (click)="cancelarRenombrar()" [disabled]="guardando" title="Cancelar">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    </div>
  </ng-template>

  <div class="ed" *ngIf="row as r">
    <!-- El motivo real del servidor, no un genérico: «La épica tiene 9 tarea(s) asociada(s)» le
         dice al usuario qué hacer; «no se pudo» le dice que lo intente otra vez. -->
    <p class="ed-error" *ngIf="errorMsg">
      <i class="fa-solid fa-triangle-exclamation"></i> {{ errorMsg }}
    </p>

    <!-- Avance -->
    <div class="ed-work">
      <div class="ed-work-top">
        <span class="ed-work-lbl">Avance del trabajo</span>
        <span class="ed-work-num">{{ r.doneCount }}/{{ r.taskCount }} tareas · {{ progress }}%</span>
      </div>
      <div class="ed-work-bar"><div class="ed-work-fill" [style.width.%]="progress" [style.background]="accent"></div></div>
      <div class="ed-chips">
        <span class="ed-chip" *ngIf="r.points"><i class="fa-solid fa-weight-hanging"></i> {{ r.points }} pts</span>
        <span class="ed-chip" *ngFor="let a of r.epic.areas" [style.background]="a.color + '1a'" [style.color]="a.color">
          <i class="fa-solid fa-code-branch"></i> {{ a.name }}
        </span>
      </div>
    </div>

    <!-- El reloj. Es el bloque grande a propósito: son los dos números que la barra del Gantt
         codifica y que no se explican en ninguna otra parte. -->
    <div class="ed-clock">
      <div class="ed-clock-cell">
        <span class="ed-n" [style.color]="accent">{{ trabajo ?? '—' }}</span>
        <span class="ed-k">de trabajo</span>
        <span class="ed-h">lo que sus tareas estuvieron en curso, sin contar dos veces lo que corrió en paralelo</span>
      </div>
      <div class="ed-clock-cell">
        <span class="ed-n">{{ abierta ?? '—' }}</span>
        <span class="ed-k">abierta</span>
        <span class="ed-h">de la primera tarea a la última, huecos incluidos</span>
      </div>
      <div class="ed-clock-cell" *ngIf="pausa as p">
        <span class="ed-n warn">{{ p }}</span>
        <span class="ed-k">sin tocarse</span>
        <span class="ed-h">la diferencia entre las dos: días que la épica estuvo abierta y parada</span>
      </div>
    </div>

    <div class="ed-dates">
      <span *ngIf="r.startedAt"><i class="fa-regular fa-flag"></i> Arrancó el {{ r.startedAt | date: 'd MMM yyyy' }}</span>
      <span *ngIf="r.endedAt"><i class="fa-regular fa-circle-check"></i> Cerró el {{ r.endedAt | date: 'd MMM yyyy' }}</span>
      <span class="live" *ngIf="enVuelo"><i class="fa-solid fa-bolt"></i> Sigue abierta: le faltan tareas por liberar</span>
      <span class="muted" *ngIf="!r.startedAt"><i class="fa-regular fa-clock"></i> Todavía sin arrancar</span>
    </div>

    <!-- De qué va -->
    <div class="ed-block" *ngIf="r.epic.value || r.epic.summary">
      <span class="ed-label"><i class="fa-solid fa-align-left" [style.color]="accent"></i> De qué va</span>
      <p class="ed-text">{{ r.epic.value || r.epic.summary }}</p>
    </div>

    <!-- Las tareas. El motivo por el que esta ficha existe: una épica ES su desglose. -->
    <div class="ed-block">
      <span class="ed-label">
        <i class="fa-solid fa-list-check" [style.color]="accent"></i> Las tareas que la componen
        <span class="ed-count" *ngIf="tasks?.length">{{ tasks?.length }}</span>
      </span>

      <div class="ed-tasks" *ngIf="loading">
        <div class="ed-task sk" *ngFor="let s of [1, 2, 3]">
          <p-skeleton height="0.8rem" width="60%"></p-skeleton>
          <p-skeleton height="0.7rem" width="4rem"></p-skeleton>
        </div>
      </div>

      <div class="ed-tasks" *ngIf="!loading && tasks?.length">
        <button type="button" class="ed-task" *ngFor="let f of tasks" (click)="openItem.emit(f.task._id)"
          [title]="'Abrir ' + (f.task.code || f.task.title)">
          <i class="ed-t-type" [class]="f.icon" [style.color]="f.color"></i>
          <span class="ed-t-txt">
            <span class="ed-t-title"><span class="ed-code" *ngIf="f.task.code">{{ f.task.code }}</span>{{ f.task.title }}</span>
            <span class="ed-t-sub">
              <span class="ed-dot" [style.background]="f.estadoColor"></span>
              {{ f.estado }}
              <span class="ed-t-area" *ngFor="let a of f.areas" [style.color]="a.color">{{ a.label }}</span>
            </span>
          </span>
          <span class="ed-t-right">
            <span class="ed-t-pts" *ngIf="f.task.points">{{ f.task.points }} pts</span>
            <span class="ed-t-dur" [class.muted]="!f.medida">{{ f.duracion }}</span>
          </span>
          <i class="fa-solid fa-chevron-right ed-t-go"></i>
        </button>
      </div>

      <p class="ed-empty" *ngIf="!loading && tasks && !tasks.length">
        Sin tareas vinculadas todavía. Una épica sin tareas no tiene estado ni tiempo que medir.
      </p>
    </div>

    <!-- Retirar. Abajo del todo y a propósito: se llega aquí después de haber visto que no tiene
         trabajo. Cuando sí lo tiene no se ofrece el botón deshabilitado —que invita a pelearse con
         él— sino la razón y qué hacer. -->
    <div class="ed-danger" *ngIf="isDev">
      <ng-container *ngIf="!confirmandoRetiro">
        <button type="button" class="ed-retire" *ngIf="puedeRetirar" (click)="pedirRetiro()">
          <i class="fa-solid fa-trash-can"></i> Retirar esta épica
        </button>
        <p class="ed-why" *ngIf="motivoNoRetirar as motivo">
          <i class="fa-solid fa-lock"></i> {{ motivo }}
        </p>
      </ng-container>

      <div class="ed-confirm" *ngIf="confirmandoRetiro">
        <span class="ed-confirm-q">
          <i class="fa-solid fa-triangle-exclamation"></i>
          ¿Retirar «{{ r.epic.title }}»? Desaparece de la lista al clasificar una tarea y del Roadmap.
        </span>
        <span class="ed-confirm-btns">
          <button type="button" class="ed-cancel-btn" (click)="cancelarRetiro()" [disabled]="guardando">Cancelar</button>
          <button type="button" class="ed-confirm-btn" (click)="retirar()" [disabled]="guardando">
            <i class="fa-solid" [class.fa-trash-can]="!guardando" [class.fa-spinner]="guardando" [class.fa-spin]="guardando"></i>
            Retirar
          </button>
        </span>
      </div>
    </div>
  </div>
</p-dialog>
